import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import PropTypes from 'prop-types';

import axiosInstance from '../../api';
import { authContext } from '../../routes';
import SERVER_API_URL from '../../config';
import Iconify from '../../components/iconify';
import ChurchDataGrid from '../common/ChurchDataGrid';
import { ethDate } from '../../utils/ethDate';

export default function ChurchList({ setUserToBeUpdated, setChurchToBeUpdated, setChurchActivatedOn }) {
  const { user } = useContext(authContext);
  const [churchList, setChurchList] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedChurch, setSelectedChurch] = useState();

  const columns = [
    { headerClassName: 'super-app-theme--header', field: 'name', width: 200, headerName: 'የአጥቢያ ስም' },
    {
      headerClassName: 'super-app-theme--header', field: 'district', width: 150, headerName: 'የወረዳ ስም',
      valueGetter: (params) => params.row.district.name
    },
    { headerClassName: 'super-app-theme--header', field: 'description', width: 150, headerName: 'የአጥቢያ መግለጫ' },
    { headerClassName: 'super-app-theme--header', field: 'history', width: 100, headerName: 'የአጥቢያ ታሪክ' },
    { headerClassName: 'super-app-theme--header', field: 'type', width: 150, headerName: 'ዓይነት' },
    { headerClassName: 'super-app-theme--header', field: 'usersCount', width: 100, headerName: 'ተከታዮች' },
    { headerClassName: 'super-app-theme--header', field: 'phone', width: 150, headerName: 'ስልክ ቁጥር' },
    {
      headerClassName: 'super-app-theme--header',
      field: 'email', width: 200, headerName: 'ኢሜይል'
    },
    {
      headerClassName: 'super-app-theme--header',
      field: 'actions',
      width: 120,
      headerName: 'ድርጊቶች',
      renderCell: (params) => {
        const handleUserInfo = async () => {
          try {
            const response = await axiosInstance.post(`${SERVER_API_URL}/user/findManyByChurchId/${params.id}`);
            if (response.status === 200) {
              setUserToBeUpdated(response.data);
              setChurchActivatedOn('churchUserList');
            }
          } catch (error) {
            // console.log(error)
          }
        };

        const handleEdit = async () => {
          try {
            const response = await axiosInstance.get(`${SERVER_API_URL}/church/findOneById/${params.id}`);
            if (response.status === 200) {
              setChurchToBeUpdated(response.data);
              setChurchActivatedOn('churchUpdate');
            }
          } catch (error) {
            // console.log(error)
          }
        };

        const handleDelete = async () => {
          try {
            const response = await axiosInstance.get(`${SERVER_API_URL}/church/findOneById/${params.id}`);
            if (response.status === 200) {
              setSelectedChurch(response.data);
              setOpenDelete(true);
            }
          } catch (error) {
            // console.log(error)
          }
        };

        return (
          <>
            <Tooltip title="ተከታዮች" color="success" arrow>
              <IconButton disabled={!(user && user.role.permissions.user.read)} onClick={handleUserInfo}>
                <Iconify icon="fluent:people-team-16-filled" />
              </IconButton>
            </Tooltip>
            <Tooltip title="አርትዕ" color="warning" arrow>
              <IconButton disabled={!(user && user.role.permissions.church.update)} onClick={handleEdit}>
                <Edit sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="ሰርዝ" color="error" arrow>
              <IconButton disabled={!(user && user.role.permissions.church.delete)} onClick={handleDelete}>
                <Delete sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
    {
      headerClassName: 'super-app-theme--header',
      field: 'createdAt',
      headerName: 'የተመዘገበበት ቀን',
      width: 180,
      valueGetter: (params) => ethDate(params.value),
    },
    {
      headerClassName: 'super-app-theme--header',
      field: 'updatedAt',
      headerName: 'የተስተካከለበት ቀን',
      width: 180,
      valueGetter: (params) => ethDate(params.value),
    },
  ];

  const handleOk = async () => {
    try {
      const response = await axiosInstance.delete(`${SERVER_API_URL}/church/deleteStatusOneById/${selectedChurch.id}`);
      if (response.status === 200) {
        getAllChurch();
        setOpenDelete(false);
      }
    } catch (error) {
      // console.log(error)
    }
  };

  const getAllChurch = useCallback(async () => {
    try {
      let response;
      if (user && user.role.permissions.dioceseLevel.read) {
        response = await axiosInstance.get(`${SERVER_API_URL}/church/findAll`);
      } else {
        response = await axiosInstance.get(
          `${SERVER_API_URL}/church/findManyByDistrictId/${user && user.church.district.id}`
        );
      }
      if (response.status === 200) {
        setChurchList(response.data);
      }
    } catch (error) {
      // console.log(error);
    }
  }, [user, setChurchList]);

  useEffect(() => {
    getAllChurch();
  }, [getAllChurch]);

  return (
    <>
      <ChurchDataGrid columns={columns} churchList={churchList} />
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <Box sx={{ backgroundColor: (theme) => theme.palette.mode === 'light' ? '#F9F9FB' : '#272424' }}> <DialogTitle>እርግጠኛ ነህ {selectedChurch && selectedChurch.name} አጥቢያን መሰረዝ ትፈልጋለህ?</DialogTitle>
          <DialogContent>
            <DialogContentText>በዚህ አጥቢያ ስር ያሉ ሁሉም ተጠቃሚዎች ይጠፋሉ!</DialogContentText>
            <DialogContentText>ይህ እርምጃ ሊቀለበስ አይችልም።</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDelete(false)}>ተመለስ</Button>
            <Button color="error" variant="contained" onClick={handleOk} autoFocus>
              ሰርዝ
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}

ChurchList.propTypes = {
  setUserToBeUpdated: PropTypes.func.isRequired,
  setChurchToBeUpdated: PropTypes.func.isRequired,
  setChurchActivatedOn: PropTypes.func.isRequired,
};
